import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Gift, Plus, Trash2, Tag as TagIcon, Palette, Pencil, FlaskConical, X, Search, ChevronDown } from "lucide-react";
import UsageTip from "@/components/UsageTip";
import { fuzzyFilter } from "@/lib/fuzzySearch";

const cop = (v) => `$ ${(Number(v) || 0).toLocaleString("es-CO")}`;

const EMPTY_FORM = {
  nombre: "",
  aplica_a_tipo: "producto",
  aplica_a_id: "",
  cantidad_minima: 500,
  descuento_pct: 5,
  plazo_dias: 30,
  alcance_descuento: "solo_producto",
  monto_minimo_pedido: "",
  vigencia_desde: "",
  vigencia_hasta: "",
};

export default function ReglasProntoPagoPage({ embedded = false }) {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const [reglas, setReglas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null); // null = crear, string = editar
  const [openCat, setOpenCat] = useState(false);
  const [openSim, setOpenSim] = useState(false);
  const [nuevaCat, setNuevaCat] = useState({ nombre: "", color: "#E6007E" });
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Simulador
  const [simItems, setSimItems] = useState([{ producto_id: "", cantidad: 1 }]);
  const [simResult, setSimResult] = useState(null);
  const [simulando, setSimulando] = useState(false);
  // Buscador de categorías (reemplaza el "chip dump" desordenado)
  const [catQuery, setCatQuery] = useState("");
  const [catListOpen, setCatListOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/reglas-pronto-pago"),
        api.get("/productos?limit=500"),
        api.get("/categorias"),
      ]);
      setReglas(r1.data);
      setProductos(r2.data.filter((p) => !p.es_padre) || []);
      setCategorias(r3.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guardar = async () => {
    if (form.aplica_a_tipo !== "todos" && !form.aplica_a_id) return toast.error("Selecciona el producto/categoría");
    setSaving(true);
    try {
      const payload = {
        aplica_a_tipo: form.aplica_a_tipo,
        aplica_a_id: form.aplica_a_id || null,
        cantidad_minima: Number(form.cantidad_minima),
        descuento_pct: Number(form.descuento_pct),
        plazo_dias: Number(form.plazo_dias),
        alcance_descuento: form.alcance_descuento,
        activa: true,
        nombre: form.nombre || null,
        monto_minimo_pedido: form.monto_minimo_pedido ? Number(form.monto_minimo_pedido) : null,
        vigencia_desde: form.vigencia_desde || null,
        vigencia_hasta: form.vigencia_hasta || null,
      };
      if (editId) {
        await api.patch(`/reglas-pronto-pago/${editId}`, payload);
        toast.success("Regla actualizada");
      } else {
        await api.post("/reglas-pronto-pago", payload);
        toast.success("Regla creada");
      }
      setOpen(false);
      setEditId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const abrirCrear = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const abrirEditar = (r) => {
    setEditId(r.id);
    setForm({
      nombre: r.nombre || "",
      aplica_a_tipo: r.aplica_a_tipo || (r.producto_id ? "producto" : "todos"),
      aplica_a_id: r.aplica_a_id || r.producto_id || "",
      cantidad_minima: r.cantidad_minima || 1,
      descuento_pct: r.descuento_pct || 5,
      plazo_dias: r.plazo_dias || 30,
      alcance_descuento: r.alcance_descuento || "solo_producto",
      monto_minimo_pedido: r.monto_minimo_pedido || "",
      vigencia_desde: r.vigencia_desde || "",
      vigencia_hasta: r.vigencia_hasta || "",
    });
    setOpen(true);
  };

  const abrirSimulador = () => {
    setSimItems([{ producto_id: "", cantidad: 1 }]);
    setSimResult(null);
    setOpenSim(true);
  };

  const simular = async () => {
    const validItems = simItems.filter((it) => it.producto_id && Number(it.cantidad) > 0);
    if (validItems.length === 0) return toast.error("Agrega al menos un producto con cantidad");
    setSimulando(true);
    try {
      const { data } = await api.post("/reglas-pronto-pago/simular",
        validItems.map((it) => ({ producto_id: it.producto_id, cantidad: Number(it.cantidad) })));
      setSimResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al simular");
    } finally { setSimulando(false); }
  };

  const archivar = async (r) => {
    if (!window.confirm(`¿Archivar la regla?`)) return;
    try {
      await api.delete(`/reglas-pronto-pago/${r.id}`);
      toast.success("Regla archivada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const crearCategoria = async () => {
    if (!nuevaCat.nombre.trim()) return toast.error("Escribe el nombre");
    try {
      await api.post("/categorias", { nombre: nuevaCat.nombre.trim(), color: nuevaCat.color });
      toast.success("Categoría creada");
      setOpenCat(false);
      setNuevaCat({ nombre: "", color: "#E6007E" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const eliminarCat = async (c) => {
    if (!window.confirm(`¿Eliminar categoría "${c.nombre}"? Los productos quedarán sin categoría.`)) return;
    try {
      await api.delete(`/categorias/${c.id}`);
      toast.success("Categoría eliminada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      {!embedded && (
        <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-primary mb-2">Descuentos</div>
            <h1 className="text-4xl font-display font-bold text-foreground flex items-center gap-3">
              <Gift className="w-8 h-8 text-primary" /> Reglas de pronto pago
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Descuentos condicionales por volumen, producto o categoría.</p>
          </div>
        </header>
      )}

      <UsageTip storageKey="reglas-pronto-pago-v2" steps={[
        "Elige a qué <b>aplica</b> la regla: un producto, una <b>categoría</b> (ej. cintas, papel coreano) o todos.",
        "Condiciones: cantidad mínima, monto mínimo del pedido, fecha de vigencia (para promos temporales).",
        "El descuento aplica <b>solo si el cliente paga completo antes del plazo</b>.",
      ]} />

      <div className="flex gap-2 mb-4 flex-wrap">
        {isDirector && (
          <Button onClick={abrirCrear} className="h-10 bg-primary hover:bg-primary/90" data-testid="new-regla-pp-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva regla
          </Button>
        )}
        {isDirector && (
          <Button variant="outline" onClick={() => setOpenCat(true)} className="h-10" data-testid="new-categoria-btn">
            <TagIcon className="w-4 h-4 mr-2" /> Nueva categoría
          </Button>
        )}
        <Button variant="outline" onClick={abrirSimulador} className="h-10" data-testid="simulador-btn">
          <FlaskConical className="w-4 h-4 mr-2" /> Simulador
        </Button>
      </div>

      {/* Categorías existentes: buscador colapsable con dropdown (evita el mural de chips) */}
      {categorias.length > 0 && (
        <div className="mb-6" data-testid="categorias-panel">
          <button
            type="button"
            onClick={() => setCatListOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 h-11 rounded-xl border border-border bg-card hover:border-primary/40 text-sm"
            data-testid="categorias-toggle"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <TagIcon className="w-4 h-4" />
              <span><b className="text-foreground">{categorias.length}</b> categorías configuradas</span>
            </span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${catListOpen ? "rotate-180" : ""}`} />
          </button>
          {catListOpen && (() => {
            const filtradas = fuzzyFilter(categorias, catQuery, ["nombre"]);
            return (
              <div className="mt-2 border border-border rounded-xl bg-card" data-testid="categorias-dropdown">
                <div className="relative p-3 border-b border-border">
                  <Search className="w-4 h-4 absolute left-6 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                  <Input
                    autoFocus
                    value={catQuery}
                    onChange={(e) => setCatQuery(e.target.value)}
                    placeholder={`Buscar entre ${categorias.length} categorías...`}
                    className="pl-9 h-10"
                    data-testid="categorias-search"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-border/60">
                  {filtradas.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-muted-foreground italic">
                      No hay categorías que coincidan con "{catQuery}".
                    </div>
                  ) : filtradas.map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/40"
                      data-testid={`cat-row-${c.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || "#E6007E" }} />
                        <span className="text-sm font-medium truncate">{c.nombre}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">· {c.productos_count || 0} prod.</span>
                      </div>
                      {isDirector && (
                        <button
                          onClick={() => eliminarCat(c)}
                          className="p-1 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600 flex-shrink-0"
                          data-testid={`del-cat-${c.id}`}
                          title="Eliminar categoría"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-4">Aplica a</div>
          <div className="col-span-2 text-right">Cant. mín.</div>
          <div className="col-span-1 text-right">%</div>
          <div className="col-span-2 text-center">Alcance</div>
          <div className="col-span-2 text-right">Plazo/Vigencia</div>
          <div className="col-span-1 text-right">Acciones</div>
        </div>
        {reglas.length === 0 && (
          <div className="px-6 py-16 text-center">
            <Gift className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <div className="mt-3 text-sm text-muted-foreground">Aún no hay reglas de pronto pago.</div>
          </div>
        )}
        {reglas.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-muted/40" data-testid={`regla-row-${r.id}`}>
            <div className="col-span-4">
              <div className="text-sm font-medium text-foreground">
                {r.aplica_a_tipo === "producto" && `📦 ${r.producto_nombre || r.aplica_a_id}`}
                {r.aplica_a_tipo === "categoria" && `🏷️ ${r.categoria_nombre || r.aplica_a_id}`}
                {r.aplica_a_tipo === "todos" && "🌐 Todos los productos"}
                {!r.aplica_a_tipo && r.producto_nombre}
              </div>
              {r.nombre && <div className="text-[11px] text-muted-foreground">{r.nombre}</div>}
              {r.monto_minimo_pedido && (
                <div className="text-[10px] text-muted-foreground">Pedido ≥ {cop(r.monto_minimo_pedido)}</div>
              )}
            </div>
            <div className="col-span-2 text-right text-sm tabular-nums">{Number(r.cantidad_minima).toLocaleString("es-CO")}u</div>
            <div className="col-span-1 text-right text-sm font-bold text-primary">{r.descuento_pct}%</div>
            <div className="col-span-2 text-center text-[11px]">
              {(r.alcance_descuento || "solo_producto") === "solo_producto" ? "Solo producto" : "Todo pedido"}
            </div>
            <div className="col-span-2 text-right text-xs">
              <div>{r.plazo_dias} días</div>
              {(r.vigencia_desde || r.vigencia_hasta) && (
                <div className="text-[10px] text-muted-foreground">{r.vigencia_desde || "—"} → {r.vigencia_hasta || "—"}</div>
              )}
            </div>
            <div className="col-span-1 flex justify-end gap-1 items-center">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold uppercase ${r.activa ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}>
                {r.activa ? "Activa" : "Off"}
              </span>
              {isDirector && (
                <button onClick={() => abrirEditar(r)} className="p-1 rounded-md hover:bg-primary/10 text-muted-foreground/70 hover:text-primary" data-testid={`editar-regla-${r.id}`} title="Editar">
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {isDirector && r.activa && (
                <button onClick={() => archivar(r)} className="p-1 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600" data-testid={`archivar-regla-${r.id}`} title="Archivar">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal crear/editar regla */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditId(null); }}>
        <DialogContent className="max-w-2xl" data-testid="regla-pp-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Editar regla de descuento" : "Nueva regla de descuento"}</DialogTitle>
            <p className="text-sm text-muted-foreground">Descuento condicionado por volumen, con pronto pago.</p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Nombre (opcional)</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Promo cintas volumen · Marzo"
                className="h-11 mt-1" data-testid="regla-pp-nombre" />
            </div>
            <div>
              <Label>¿A qué aplica?*</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { v: "producto", l: "📦 Producto" },
                  { v: "categoria", l: "🏷️ Categoría" },
                  { v: "todos", l: "🌐 Todos" },
                ].map((o) => (
                  <button
                    key={o.v} type="button"
                    onClick={() => setForm({ ...form, aplica_a_tipo: o.v, aplica_a_id: "" })}
                    className={`h-11 rounded-lg border text-sm font-semibold transition-all ${form.aplica_a_tipo === o.v ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}
                    data-testid={`regla-pp-tipo-${o.v}`}
                  >{o.l}</button>
                ))}
              </div>
            </div>
            {form.aplica_a_tipo === "producto" && (
              <div>
                <Label>Producto*</Label>
                <select value={form.aplica_a_id} onChange={(e) => setForm({ ...form, aplica_a_id: e.target.value })}
                  className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="regla-pp-producto">
                  <option value="">Selecciona...</option>
                  {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre} {p.sku ? `(${p.sku})` : ""}</option>)}
                </select>
              </div>
            )}
            {form.aplica_a_tipo === "categoria" && (
              <div>
                <Label>Categoría*</Label>
                <select value={form.aplica_a_id} onChange={(e) => setForm({ ...form, aplica_a_id: e.target.value })}
                  className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm" data-testid="regla-pp-categoria">
                  <option value="">Selecciona...</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.productos_count || 0} productos)</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Cant. mínima*</Label>
                <Input type="number" min="1" value={form.cantidad_minima}
                  onChange={(e) => setForm({ ...form, cantidad_minima: e.target.value })}
                  className="h-11 mt-1 tabular-nums" data-testid="regla-pp-cant-min" />
              </div>
              <div>
                <Label>Descuento %*</Label>
                <Input type="number" min="0.1" max="100" step="0.5" value={form.descuento_pct}
                  onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })}
                  className="h-11 mt-1 tabular-nums" data-testid="regla-pp-pct" />
              </div>
              <div>
                <Label>Plazo (días)*</Label>
                <Input type="number" min="1" max="180" value={form.plazo_dias}
                  onChange={(e) => setForm({ ...form, plazo_dias: e.target.value })}
                  className="h-11 mt-1 tabular-nums" data-testid="regla-pp-plazo" />
              </div>
            </div>
            <div>
              <Label>Alcance del descuento</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[
                  { v: "solo_producto", l: "Solo lo que califica" },
                  { v: "todo_pedido", l: "Todo el pedido" },
                ].map((o) => (
                  <button key={o.v} type="button"
                    onClick={() => setForm({ ...form, alcance_descuento: o.v })}
                    className={`h-10 rounded-lg border text-sm ${form.alcance_descuento === o.v ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:border-primary/40"}`}
                    data-testid={`regla-pp-alcance-${o.v}`}
                  >{o.l}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Monto mín. pedido</Label>
                <MoneyInput value={form.monto_minimo_pedido}
                  onValueChange={(v) => setForm({ ...form, monto_minimo_pedido: v })}
                  placeholder="Opcional" className="h-11 mt-1" data-testid="regla-pp-monto-min" />
              </div>
              <div>
                <Label>Vigencia desde</Label>
                <Input type="date" value={form.vigencia_desde}
                  onChange={(e) => setForm({ ...form, vigencia_desde: e.target.value })}
                  className="h-11 mt-1" data-testid="regla-pp-vig-desde" />
              </div>
              <div>
                <Label>Vigencia hasta</Label>
                <Input type="date" value={form.vigencia_hasta}
                  onChange={(e) => setForm({ ...form, vigencia_hasta: e.target.value })}
                  className="h-11 mt-1" data-testid="regla-pp-vig-hasta" />
              </div>
            </div>
            <Button onClick={guardar} disabled={saving} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="regla-pp-save">
              {saving ? "Guardando..." : (editId ? "Guardar cambios" : "Crear regla")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal crear categoría */}
      <Dialog open={openCat} onOpenChange={setOpenCat}>
        <DialogContent data-testid="categoria-modal">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nueva categoría</DialogTitle></DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Nombre*</Label>
              <Input value={nuevaCat.nombre} onChange={(e) => setNuevaCat({ ...nuevaCat, nombre: e.target.value })}
                placeholder="Ej: Cintas, Papeles Coreanos, Agujas"
                className="h-11 mt-1" data-testid="cat-nombre" />
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="color" value={nuevaCat.color} onChange={(e) => setNuevaCat({ ...nuevaCat, color: e.target.value })}
                  className="h-11 w-20" />
                <span className="text-xs text-muted-foreground">Se usa para el chip visual</span>
              </div>
            </div>
            <Button onClick={crearCategoria} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="cat-save">
              Crear categoría
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Modal Simulador */}
      <Dialog open={openSim} onOpenChange={setOpenSim}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="simulador-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-primary" /> Simulador de descuentos
            </DialogTitle>
            <p className="text-sm text-muted-foreground">Arma un pedido de prueba y mira qué reglas aplican, sin crear nada.</p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label>Productos del pedido</Label>
              {simItems.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <select value={it.producto_id}
                    onChange={(e) => { const c = [...simItems]; c[idx].producto_id = e.target.value; setSimItems(c); }}
                    className="col-span-8 h-10 border border-border rounded-md px-2 bg-card text-sm" data-testid={`sim-prod-${idx}`}>
                    <option value="">Selecciona producto...</option>
                    {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre} {p.sku ? `(${p.sku})` : ""}</option>)}
                  </select>
                  <Input type="number" min="1" step="1" value={it.cantidad}
                    onChange={(e) => { const c = [...simItems]; c[idx].cantidad = e.target.value; setSimItems(c); }}
                    className="col-span-3 h-10 tabular-nums" placeholder="Cant." data-testid={`sim-cant-${idx}`} />
                  <button onClick={() => setSimItems(simItems.filter((_, i) => i !== idx))}
                    disabled={simItems.length === 1}
                    className="col-span-1 h-10 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600 disabled:opacity-30 flex items-center justify-center"
                    data-testid={`sim-remove-${idx}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={() => setSimItems([...simItems, { producto_id: "", cantidad: 1 }])}
                className="text-xs font-semibold text-primary hover:underline" data-testid="sim-add-item">
                + Agregar otro producto
              </button>
            </div>
            <Button onClick={simular} disabled={simulando} className="w-full h-11 bg-primary hover:bg-primary/90" data-testid="sim-run">
              {simulando ? "Calculando..." : "Simular descuentos"}
            </Button>

            {simResult && (
              <div className="mt-4 rounded-lg border border-primary/30 bg-gradient-to-br from-primary/5 to-transparent p-4" data-testid="sim-result">
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Subtotal</div>
                    <div className="text-lg font-bold tabular-nums">{cop(simResult.subtotal_pedido)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Descuento potencial</div>
                    <div className="text-lg font-bold tabular-nums text-emerald-700">−{cop(simResult.total_descuento)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total con dcto.</div>
                    <div className="text-lg font-bold tabular-nums text-primary">{cop(simResult.total_con_descuento)}</div>
                  </div>
                </div>
                {simResult.plazo_dias > 0 && (
                  <div className="text-xs text-muted-foreground mb-3">
                    ⏱ El cliente debe pagar en máximo <b>{simResult.plazo_dias} días</b> para conservar el descuento.
                  </div>
                )}
                {simResult.reglas_aplicadas.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">Ninguna regla activa aplica a este carrito.</div>
                ) : (
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Reglas que aplican</div>
                    <div className="space-y-1.5">
                      {simResult.reglas_aplicadas.map((r, i) => (
                        <div key={i} className="flex justify-between items-center text-xs p-2 bg-card border border-border/60 rounded-md">
                          <div>
                            <div className="font-semibold">{r.regla_nombre}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {r.aplica_a_tipo} · {r.cantidad_matching}u de {r.cantidad_minima_regla}u · {r.alcance === "todo_pedido" ? "Todo el pedido" : "Solo lo que califica"}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-primary">−{cop(r.monto)}</div>
                            <div className="text-[10px] text-muted-foreground">{r.descuento_pct}%</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
